// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';

export type PlaceholderTemplateDismissedFlag = {
	hasDismissed: boolean;
} | null;

type UserPropertyResponse = {
	key?: string;
	value?: unknown;
};

const getUserPropertyUrl = (accountId: string, propertyKey: string): string =>
	`/rest/api/latest/user/properties/${propertyKey}?accountId=${encodeURIComponent(accountId)}`;

const fetchDismissedFlag = async (
	accountId: string,
	propertyKey: string,
	analyticsId: string,
): Promise<PlaceholderTemplateDismissedFlag> => {
	try {
		const response = await performGetRequest<UserPropertyResponse>(
			getUserPropertyUrl(accountId, propertyKey),
		);
		return { hasDismissed: !!response?.value };
	} catch (error) {
		if (error instanceof FetchError) {
			// 404: API deliberately returns 404 if user property is not found,
			// which implies the property has not been set => not dismissed.
			if (error.statusCode === 404) {
				return { hasDismissed: false };
			}
			// 401/403: stay conservative and treat as dismissed so we don't
			// flash the placeholder for users that can't read their own props.
			if (error.statusCode === 401 || error.statusCode === 403) {
				return { hasDismissed: true };
			}
		}
		fireErrorAnalytics({
			error: error instanceof Error ? error : undefined,
			meta: {
				id: analyticsId,
				packageName: 'jiraPlaceholderTemplateDismissedResource',
				teamName: 'issue-view-pulsar',
			},
		});
		return null;
	}
};

export const createPlaceholderDismissedResource = (
	type: string,
	propertyKey: string,
	analyticsId: string,
): RouteResource<PlaceholderTemplateDismissedFlag> =>
	createResource<PlaceholderTemplateDismissedFlag>({
		type,
		getKey: (_routerContext, { tenantContext }) => {
			const accountId = tenantContext?.atlassianAccountId ?? 'current-account-id';
			return `${accountId}:${propertyKey}`;
		},
		getData: async (_routerContext, { tenantContext }) => {
			const accountId = tenantContext?.atlassianAccountId;
			if (!accountId || tenantContext?.isAnonymous) {
				return null;
			}
			return fetchDismissedFlag(accountId, propertyKey, analyticsId);
		},
	});
